import { useMemo } from "react"
import type { ChatAttachmentRejectionReason, ChatAttachmentsConfig } from "@mui/x-chat-headless"
import { useNotifications } from "hooks/notifications"

export const MAX_ATTACHMENT_COUNT = 5
export const MAX_ATTACHMENT_BYTES = 50 * 1024 * 1024

export type AttachmentRejectionReason = ChatAttachmentRejectionReason | "folder"

export interface AttachmentRejection {
  file: File
  reason: AttachmentRejectionReason
}

export interface KellyAttachmentsConfig extends Omit<ChatAttachmentsConfig, "onAttachmentReject"> {
  onAttachmentReject?: (rejections: AttachmentRejection[]) => void
}

export const ATTACHMENT_REJECTION_MESSAGES: Record<AttachmentRejectionReason, string> = {
  "file-size": "Files must be 50 MB or smaller.",
  "file-count": "You can attach up to 5 files per message.",
  "mime-type": "That file type is not accepted.",
  folder: "Folders can't be attached. Drop the files inside them instead.",
}

export const INVALID_ATTACHMENT_FILENAME_MESSAGE = "Rename this file. Its filename contains unsupported characters, ends in a dot or a space, or exceeds 255 characters."

const FORBIDDEN_FILENAME_CHARACTERS = ["<", ">", ":", '"', "|", "?", "*", "\\", "/"]

// pi-server's isAttachmentFilenameValid applies the same rule. The two must agree or a file passes here and
// fails at upload.
function isControlCharacterCode(code: number): boolean {
  return code < 0x20 || (code >= 0x7f && code <= 0x9f)
}

export function isAttachmentFilenameValid(filename: string): boolean {
  if (filename.length < 1 || filename.length > 255) return false
  if (filename.endsWith(".") || filename.endsWith(" ")) return false
  for (let index = 0; index < filename.length; index += 1) {
    if (FORBIDDEN_FILENAME_CHARACTERS.includes(filename[index])) return false
    if (isControlCharacterCode(filename.charCodeAt(index))) return false
  }
  return true
}

export const ATTACHMENTS_CONFIG: ChatAttachmentsConfig = {
  maxFileCount: MAX_ATTACHMENT_COUNT,
  maxFileSize: MAX_ATTACHMENT_BYTES,
}

export function useAttachmentsConfig(): KellyAttachmentsConfig {
  const notifications = useNotifications()
  return useMemo(
    () => ({
      ...ATTACHMENTS_CONFIG,
      onAttachmentReject: (rejections: AttachmentRejection[]) => {
        const reasons = new Set(rejections.map((rejection) => rejection.reason))
        for (const reason of reasons) notifications.error(ATTACHMENT_REJECTION_MESSAGES[reason])
      },
    }),
    [notifications],
  )
}
